import { CloseOutlined, WarningOutlined } from '@ant-design/icons';
import { default as Input } from 'antd/es/input';
import { default as Select } from 'antd/es/select';
import { default as Switch } from 'antd/es/switch';
import type { ReactNode } from 'react';
import { Controller } from 'react-hook-form';

import type { Webhook } from 'api';
import { Restricted } from 'containers/Restricted';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';
import { TertiaryButton } from '@atoms/buttons/TertiaryButton';

import {
  AUTH_SCHEMA_DISABLED_ERROR,
  EDIT_WEBHOOK_BUSINESS_UNIT_OPTION_TEST_ID,
  EDIT_WEBHOOK_BUSINESS_UNIT_TEST_ID,
  EDIT_WEBHOOK_CANCEL_TEST_ID,
  EDIT_WEBHOOK_CLOSE_TEST_ID,
  EDIT_WEBHOOK_ENDPOINT_ERROR_TEST_ID,
  EDIT_WEBHOOK_ENDPOINT_TEST_ID,
  EDIT_WEBHOOK_FORM_TEST_ID,
  EDIT_WEBHOOK_STATUS_LABEL_TEST_ID,
  EDIT_WEBHOOK_STATUS_TOGGLE_TEST_ID,
  EDIT_WEBHOOK_SUBMIT_TEST_ID,
  EDIT_WEBHOOK_TITLE,
  EDIT_WEBHOOK_WARNING_TEST_ID,
  INACTIVE_WARNING,
  EDIT_WEBHOOK_X_AUTH_ERROR_TEST_ID,
  EDIT_WEBHOOK_X_AUTH_OPTION_TEST_ID,
  EDIT_WEBHOOK_X_AUTH_TEST_ID
} from './constants';
import { useEditWebhookForm } from './useEditWebhookForm';

import {
  BUSINESS_UNIT_PLACEHOLDER,
  ENDPOINT_URL_PLACEHOLDER,
  X_AUTH_PLACEHOLDER
} from '../AddWebhookModal/constants';
import { AuthKeyOption } from '../AuthKeyOption';
import { BusinessUnitOption } from '../BusinessUnitOption';
import { FieldErrorLine } from '../FieldErrorLine';

type EditWebhookFormProps = {
  webhook: Webhook;
  onClose: () => void;
  onCancel: () => void;
  onSaved: () => void;
};

const FieldLabel = ({ children }: { children: ReactNode }) => (
  <label className="mb-0.5 flex gap-0.5 text-xs font-medium text-gray-500">
    {children}
    <span className="text-feedback-error">*</span>
  </label>
);

export const EditWebhookForm = ({
  webhook,
  onClose,
  onCancel,
  onSaved
}: EditWebhookFormProps) => {
  const {
    form,
    businessUnits,
    authKeys,
    authSchemaDisabled,
    effectiveStatus,
    submit,
    isSaving
  } = useEditWebhookForm({
    webhook,
    onSaved
  });
  const {
    control,
    watch,
    formState: { errors, isDirty, isValid }
  } = form;

  const endpointUrl = watch('endpointUrl') ?? '';
  const endpointError =
    endpointUrl.length > 0 ? errors.endpointUrl?.message : undefined;

  return (
    <div
      data-testid={EDIT_WEBHOOK_FORM_TEST_ID}
      className="cool-shadow flex min-h-0 flex-col rounded-lg border border-border-muted bg-white"
    >
      <div className="flex items-center justify-between rounded-t-lg bg-white px-6 py-4">
        <span className="text-lg font-bold text-content-primary">
          {EDIT_WEBHOOK_TITLE}
        </span>
        <TertiaryButton
          data-testid={EDIT_WEBHOOK_CLOSE_TEST_ID}
          aria-label="Close panel"
          icon={<CloseOutlined className="text-base" />}
          onClick={onClose}
        />
      </div>

      <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
        <div className="flex flex-col gap-4 px-6 pb-4">
          <div className="flex flex-col">
            <FieldLabel>Endpoint URL</FieldLabel>
            <Controller
              name="endpointUrl"
              control={control}
              render={({ field }) => (
                <Input
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  data-testid={EDIT_WEBHOOK_ENDPOINT_TEST_ID}
                  placeholder={ENDPOINT_URL_PLACEHOLDER}
                  status={endpointError ? 'error' : undefined}
                  className="h-12 rounded-2xl"
                />
              )}
            />
            <FieldErrorLine
              message={endpointError}
              data-testid={EDIT_WEBHOOK_ENDPOINT_ERROR_TEST_ID}
            />
          </div>

          <div className="flex flex-col">
            <FieldLabel>Business unit</FieldLabel>
            <Controller
              name="businessUnitUuid"
              control={control}
              render={({ field }) => (
                <Select
                  showSearch
                  data-testid={EDIT_WEBHOOK_BUSINESS_UNIT_TEST_ID}
                  placeholder={BUSINESS_UNIT_PLACEHOLDER}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  filterOption={(input, option) =>
                    String(option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={businessUnits.map((unit) => ({
                    value: unit.uuid,
                    label: unit.name
                  }))}
                  optionRender={(option) => {
                    const unit = businessUnits.find(
                      (item) => item.uuid === option.value
                    );
                    return unit ? (
                      <div
                        data-testid={EDIT_WEBHOOK_BUSINESS_UNIT_OPTION_TEST_ID(
                          unit.uuid
                        )}
                      >
                        <BusinessUnitOption {...unit} />
                      </div>
                    ) : null;
                  }}
                  labelRender={(label) =>
                    label.label ?? webhook.business_unit_name
                  }
                  className="w-full"
                />
              )}
            />
          </div>

          <div className="flex flex-col">
            <FieldLabel>X-Auth-UUID</FieldLabel>
            <Controller
              name="xAuthUuid"
              control={control}
              render={({ field }) => (
                <Select
                  showSearch
                  data-testid={EDIT_WEBHOOK_X_AUTH_TEST_ID}
                  placeholder={X_AUTH_PLACEHOLDER}
                  status={authSchemaDisabled ? 'error' : undefined}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  filterOption={(input, option) =>
                    `${option?.label ?? ''} ${option?.value ?? ''}`
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={authKeys.map((key) => ({
                    value: key.uuid,
                    label: key.name
                  }))}
                  optionRender={(option) => {
                    const key = authKeys.find(
                      (item) => item.uuid === option.value
                    );
                    return key ? (
                      <div
                        data-testid={EDIT_WEBHOOK_X_AUTH_OPTION_TEST_ID(
                          key.uuid
                        )}
                      >
                        <AuthKeyOption {...key} />
                      </div>
                    ) : null;
                  }}
                  labelRender={(label) => label.label}
                  className="w-full"
                />
              )}
            />
            {authSchemaDisabled && (
              <p
                data-testid={EDIT_WEBHOOK_X_AUTH_ERROR_TEST_ID}
                className="mt-1 text-xs text-feedback-error"
              >
                {AUTH_SCHEMA_DISABLED_ERROR}
              </p>
            )}
          </div>

          <div className="mt-2 flex items-center gap-3">
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={effectiveStatus}
                  disabled={authSchemaDisabled}
                  onChange={field.onChange}
                  data-testid={EDIT_WEBHOOK_STATUS_TOGGLE_TEST_ID}
                />
              )}
            />
            <div className="flex flex-col">
              <span className="text-base font-medium text-content-primary">
                Webhook status
              </span>
              <span
                data-testid={EDIT_WEBHOOK_STATUS_LABEL_TEST_ID}
                className="text-sm italic text-content-secondary"
              >
                {effectiveStatus ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          {!effectiveStatus && (
            <div
              data-testid={EDIT_WEBHOOK_WARNING_TEST_ID}
              className="flex gap-2 rounded-2xl bg-feedback-warning-surface p-3 text-sm text-content-primary"
            >
              <WarningOutlined className="mt-0.5 shrink-0 text-feedback-attention" />
              <span>{INACTIVE_WARNING}</span>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 border-t border-border-default px-6 py-4">
          <SecondaryButton
            block
            data-testid={EDIT_WEBHOOK_CANCEL_TEST_ID}
            onClick={onCancel}
          >
            Cancel
          </SecondaryButton>
          <Restricted requiredPermission="developers:manage">
            <PrimaryButton
              block
              htmlType="submit"
              data-testid={EDIT_WEBHOOK_SUBMIT_TEST_ID}
              disabled={!isDirty || !isValid || isSaving}
              loading={isSaving}
            >
              Save changes
            </PrimaryButton>
          </Restricted>
        </div>
      </form>
    </div>
  );
};
